import Enumerable from "linq";
import React, { ReactNode, useEffect, useState } from "react";
import { SearchOutlined } from "@ant-design/icons";
import { Card, Col, Form, Modal, Row, Space } from "antd";
import { SearchParameterList } from "./SearchParameterList";
import { SearchAndFind, SearchContainerProps, SearchMode } from "./type";
import { applySearchParameters, getSearchOptionDefaultValue } from "./searchParameters";
import { translate } from "../../translate";
import { ErrorMessage } from "../ErrorMessage";
import { NOT_FOUND_MESSAGE } from "../../../App.constants";
import { SortList } from "./SortList";
import { SortableRequest } from "../../type";

export const SearchModalWithTypes = <TRequest extends object>(
  props: SearchContainerProps<TRequest> & {additonalComponents?: ReactNode[]}
) => {
  const {
    open,
    closeMe,
    searchOptions,
    title,
    findPage,
    findPageLoading,
    onComplete,
    sort,
    additonalComponents
  } = props;
  const [mode, setMode] = useState<SearchMode>(SearchMode.Search);
  const [errors, setErrors] = useState<string[]>([]);
  const [form] = Form.useForm<SearchAndFind>();

  useEffect(() => {
    form.setFieldsValue({
      parameters: searchOptions.map((so) => ({
        key: so.key,
        isActive: false,
        exactMatch: false,
        value: getSearchOptionDefaultValue(so),
      })),
      sorts: sort?.defaults ?? [],
    });
  }, []);

  const handleConfirm = async () => {
    try {
      await form.validateFields({ validateOnly: true, recursive: true });
      setErrors([]);
      form.submit();
    } catch (e: any) {
      const errors = Enumerable.from(e.errorFields)
        .selectMany((e: any) => e.errors)
        .toArray() as string[];
      setErrors(errors);
    }
  };

  const handleOnFinish = async (data: SearchAndFind) => {
    if (mode === SearchMode.Find) {
      const { page, error } = await findPage(data.id);

      if (error) {
        const [{ message }] = error;
        if (message === NOT_FOUND_MESSAGE)
          setErrors([translate(title, "not_found")]);

        return;
      }

      return onComplete(null, page);
    }

    const request = applySearchParameters(
      data.parameters,
      searchOptions,
      {} as TRequest
    );

    if (sort) {
      (request as SortableRequest).sort = data.sorts?.map((st) => ({
        field: st.field,
        sortOrder: st.direction,
      }));
    }

    onComplete(request, 1);
  };

  return (
    <Modal
      maskClosable={false}
      title={
        <Space>
          <SearchOutlined style={{ fontSize: 20 }} />
          {title}
        </Space>
      }
      closable={false}
      open={open}
      onOk={handleConfirm}
      onCancel={closeMe}
      confirmLoading={findPageLoading}
      width="40%"
    >
      <ErrorMessage
        title={translate(title, "message")}
        message={errors}
        onClose={() => setErrors([])}
      />

      <Form
        name="form"
        form={form}
        onFinish={handleOnFinish}
        onKeyUp={(e) => e.key === "Enter" && handleConfirm()}
      >
        <div className="mt-3 d-block w-100">
          <Row>
          {additonalComponents?.length && (
            additonalComponents.map((component, index) => (
              <div key={index}>
                {component}
              </div>
            ))
          )}
          </Row>
          
          <Row>
            <Col span={24}>
              <Card bordered={false}>
                <SearchParameterList
                  mode={mode}
                  form={form}
                  options={searchOptions}
                />
              </Card>
            </Col>
          </Row>

          {sort && (
            <Row className="mt-3">
              <Col span={24}>
                <Card bordered={false}>
                  <SortList fieldList={sort.fields} />
                </Card>
              </Col>
            </Row>
          )}
        </div>
      </Form>
    </Modal>
  );
};
